import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Typography } from '@mui/material';

import BackToInquiriesButton from '@components/BackToInquiriesButton/BackToInquiriesButton';
import EmptyCard from '@components/EmptyCard/EmptyCard';
import InquiryStatus from '@components/InquiryStatus/InquiryStatus';
import Loadable from '@components/Loadable/Loadable';
import PrintButton from '@components/PrintButton/PrintButton';
import { TInquiryStatus } from '@interfaces/inquiries';
import {
  IMedicationResponse,
  ISubmitMedicationNeedsInfoFormPayload,
  MedicationFormFieldNames,
} from '@interfaces/medications';
import { TSupplementResponseStatus } from '@interfaces/supplements';
import urls from '@routes/urls';
import { getInquiryMedicationsResponseUrl } from '@routes/utils';
import {
  handleGetInquiryResponse,
  handleGetMedicationResponseDetails,
} from '@store/features/inquiryResponse/actions';
import {
  inquiryResponseActiveMedicationDataSelector,
  inquiryResponseActiveMedicationErrorSelector,
  inquiryResponseActiveMedicationIdSelector,
  inquiryResponseActiveMedicationIsLoadingSelector,
  inquiryResponseDataSelector,
  inquiryResponseErrorSelector,
  inquiryResponseIsLoadingSelector,
} from '@store/features/inquiryResponse/selectors';
import {
  removeActiveMedication,
  removeInquiryResponseDetails,
} from '@store/features/inquiryResponse/slice';
import {
  handleMarkMedicationResponseAsViewed,
  handleSubmitMedicationNeedsInfoForm,
} from '@store/features/medications/userEnteredMedication/actions';
import useDispatch from '@store/hooks/useDispatch';
import { EventType, useEventBus } from '@utils/eventBus';
import formatDate from '@utils/formatDate';
import handleToast from '@utils/handleToast';

import MedicationResponseCard from './MedicationResponseCard/MedicationResponseCard';
import MedicationResponseDetailAside from './MedicationResponseDetailAside/MedicationResponseDetailAside';
import { getHasUnseenResponses, getIsLastOneThatNeedsInfo } from './utils';

import './style.scss';

const printAvailableStatuses = new Set([TInquiryStatus.Complete, TInquiryStatus.New]);

const MedicationResponsePage: FC = () => {
  const { t } = useTranslation();
  const eventBus = useEventBus('medicationResponsePage');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { inquiryId } = useParams();

  const inquiryData = useSelector(inquiryResponseDataSelector);
  const isLoading = useSelector(inquiryResponseIsLoadingSelector);
  const error = useSelector(inquiryResponseErrorSelector);
  const activeMedicationId = useSelector(inquiryResponseActiveMedicationIdSelector);
  const activeMedication = useSelector(inquiryResponseActiveMedicationDataSelector);
  const activeMedicationIsLoading = useSelector(inquiryResponseActiveMedicationIsLoadingSelector);
  const activeMedicationError = useSelector(inquiryResponseActiveMedicationErrorSelector);

  const hasUnseenResponses = useMemo(
    () => (inquiryData ? getHasUnseenResponses(inquiryData) : false),
    [inquiryData],
  );

  const isLastOneThatNeedsInfo = useMemo(
    () => (inquiryData ? getIsLastOneThatNeedsInfo(inquiryData) : false),
    [inquiryData],
  );

  const shouldShowPrintButton = useMemo(
    () => inquiryData && printAvailableStatuses.has(inquiryData.inquiryStatusId),
    [inquiryData],
  );

  const handleFetchInquiryResponse = useCallback(() => {
    if (inquiryId) {
      dispatch(handleGetInquiryResponse(+inquiryId));
    }
  }, [dispatch, inquiryId]);

  useEffect(handleFetchInquiryResponse, [handleFetchInquiryResponse]);

  useEffect(() => {
    if (activeMedication?.supplementInquiryStatusId === TSupplementResponseStatus.New) {
      dispatch(handleMarkMedicationResponseAsViewed(activeMedication.medicationInquiryId));
    }
  }, [dispatch, activeMedication]);

  const handleOpenSupplementDetails = useCallback(
    (medication: IMedicationResponse) => {
      dispatch(handleGetMedicationResponseDetails(medication.medicationInquiryId));
    },
    [dispatch],
  );

  const handleRetryOpenSupplementDetails = useCallback(() => {
    if (activeMedicationId) {
      dispatch(handleGetMedicationResponseDetails(activeMedicationId));
    }
  }, [dispatch, activeMedicationId]);

  const handleCloseMedicationDetails = useCallback(() => {
    dispatch(removeActiveMedication());
  }, [dispatch]);

  const handleSubmitNeedsInfoForm = useCallback(
    (values: { [key: string]: any }) => {
      if (!activeMedication) {
        return;
      }
      const dto: ISubmitMedicationNeedsInfoFormPayload = {
        userEnteredMedicationId: activeMedication.userEnteredMedicationId,
        name: values[MedicationFormFieldNames.Name],
        linkToProductPage: values[MedicationFormFieldNames.ProductLink],
        note: values[MedicationFormFieldNames.Note],
        images: values[MedicationFormFieldNames.Images],
      };
      dispatch(handleSubmitMedicationNeedsInfoForm(dto));
    },
    [dispatch, activeMedication],
  );

  useEffect(() => {
    eventBus.on(EventType.MEDICATION_NEEDS_INFO_FORM_SUBMITTED, () => {
      handleToast('success', t('inquiryResponse.medicationNeedsInfoFormSubmitSuccess'));
      handleCloseMedicationDetails();
      if (isLastOneThatNeedsInfo) {
        navigate(urls.inquiries);
      } else {
        handleFetchInquiryResponse();
      }
    });
    return () => eventBus.off(EventType.MEDICATION_NEEDS_INFO_FORM_SUBMITTED);
  }, [isLastOneThatNeedsInfo]);

  useEffect(
    () => () => {
      dispatch(removeInquiryResponseDetails());
      dispatch(removeActiveMedication());
    },
    [dispatch],
  );

  return (
    <Container maxWidth="lg" className="page inquiry-medication-response-page">
      <BackToInquiriesButton />

      <Typography variant="h1" className="inquiry-medication-response-page__title">
        {t('inquiryResponse.medicationTitle')}
      </Typography>

      <div className="inquiry-medication-response-page__content">
        <div className="inquiry-medication-response-page__main">
          <div className="inquiry-medication-response-page__header">
            <Typography
              variant="h2"
              className="inquiry-medication-response-page__medications-title"
            >
              {t('inquiryResponse.medicationsTitle')}
            </Typography>

            {shouldShowPrintButton && (
              <PrintButton
                buttonText={t('common.printResponses')}
                linkToPrintPage={`${getInquiryMedicationsResponseUrl(+inquiryId!)}/print`}
              />
            )}
          </div>

          <Loadable
            isLoading={!inquiryData && isLoading}
            error={error && t('errors.inquiryResponseError')}
            onRetry={handleFetchInquiryResponse}
          >
            {inquiryData && (
              <>
                <div className="inquiry-medication-response-page__inquiry">
                  <div className="inquiry-medication-response-page__inquiry-meta">
                    <div className="inquiry-medication-response-page__inquiry-name">
                      {inquiryData.name}
                    </div>
                    <div className="inquiry-medication-response-page__inquiry-date">
                      {t('inquiryResponse.submitted')} {formatDate(inquiryData.submittedOn)}
                    </div>
                  </div>

                  {inquiryData.inquiryStatusId !== TInquiryStatus.Complete && (
                    <InquiryStatus value={inquiryData.inquiryStatusId} />
                  )}
                </div>

                <div className="inquiry-medication-response-page__medications-list">
                  {inquiryData.medicationInquiriesDto.map(item => (
                    <MedicationResponseCard
                      className="inquiry-medication-response-page__medication-item"
                      data={item}
                      isSelected={item.medicationInquiryId === activeMedicationId}
                      onShowDetails={handleOpenSupplementDetails}
                      key={item.medicationInquiryId}
                    />
                  ))}
                </div>
              </>
            )}
          </Loadable>
        </div>

        {activeMedicationId ? (
          <MedicationResponseDetailAside
            className="inquiry-medication-response-page__sidebar"
            data={activeMedication}
            isLoading={activeMedicationIsLoading}
            error={activeMedicationError}
            hasUnseenResponses={hasUnseenResponses}
            isLastOneThatNeedsInfo={isLastOneThatNeedsInfo}
            onSubmitNeedsInfoForm={handleSubmitNeedsInfoForm}
            onClose={handleCloseMedicationDetails}
            onRetry={handleRetryOpenSupplementDetails}
          />
        ) : (
          <EmptyCard
            className="inquiry-medication-response-page__sidebar"
            icon="/images/img-dietarysupplement.svg"
            description={t('inquiryResponse.medicationDetailsPlaceholder')}
          />
        )}
      </div>
    </Container>
  );
};

export default MedicationResponsePage;
